import React, { useEffect, useState } from 'react';
import { usersApi } from '../services/api';
import { User, CreateUser, UpdateUser, ResetPassword } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

const UsersPage: React.FC = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CreateUser>({
    userName: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    roles: []
  });
  const [error, setError] = useState('');

  const availableRoles = ['Admin', 'User', 'Guest'];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersApi.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    try {
      if (editingUser) {
        const updateData: UpdateUser = {
          userName: formData.userName,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          roles: formData.roles
        };
        await usersApi.update(editingUser.id, updateData);
      } else {
        await usersApi.create(formData);
      }
      
      await fetchUsers();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data || 'Operation failed');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      userName: user.userName,
      email: user.email,
      password: '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      roles: user.roles
    });
    setShowModal(true);
  };

  const handleDelete = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await usersApi.delete(userId);
        await fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleResetPasswordClick = (userId: string) => {
    setResetPasswordUserId(userId);
    setNewPassword('');
    setShowResetPasswordModal(true);
    setError('');
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPasswordUserId) return;
    
    setError('');
    try {
      const resetData: ResetPassword = { newPassword };
      await usersApi.resetPassword(resetPasswordUserId, resetData);
      setShowResetPasswordModal(false);
      setNewPassword('');
      setResetPasswordUserId(null);
      // Show success message - could be enhanced with a toast notification
      const successMsg = document.createElement('div');
      successMsg.className = 'alert alert-success position-fixed top-0 end-0 m-3';
      successMsg.style.zIndex = '9999';
      successMsg.textContent = 'Password reset successfully';
      document.body.appendChild(successMsg);
      setTimeout(() => successMsg.remove(), 3000);
    } catch (err: any) {
      setError(err.response?.data || 'Failed to reset password');
    }
  };

  const resetForm = () => {
    setFormData({
      userName: '',
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      roles: []
    });
    setEditingUser(null);
    setShowModal(false);
    setError('');
  };

  const handleRoleChange = (role: string, checked: boolean) => {
    if (checked) {
      setFormData({ ...formData, roles: [...formData.roles, role] });
    } else {
      setFormData({ ...formData, roles: formData.roles.filter(r => r !== role) });
    }
  };

  if (loading) {
    return (
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>{t('user.management')}</h1>
            <button
              className="btn btn-primary"
              onClick={() => setShowModal(true)}
            >
              {t('add.user')}
            </button>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('username')}</th>
                      <th>{t('email')}</th>
                      <th>{t('name')}</th>
                      <th>{t('role')}</th>
                      <th>{t('date')}</th>
                      <th>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.userName}</td>
                        <td>{user.email}</td>
                        <td>{user.firstName} {user.lastName}</td>
                        <td>
                          {user.roles.map(role => (
                            <span key={role} className="badge bg-secondary me-1">
                              {role}
                            </span>
                          ))}
                        </td>
                        <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(user)}
                          >
                            {t('edit')}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-warning me-2"
                            onClick={() => handleResetPasswordClick(user.id)}
                          >
                            Reset Password
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(user.id)}
                          >
                            {t('delete')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingUser ? t('edit.user') : t('add.user')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={resetForm}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">{error}</div>
                  )}
                  
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">{t('username')}</label>
                    </div>
                    <div className="col-sm-9">
                      <input
                        type="text"
                        className="form-control"
                        value={formData.userName}
                        onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">{t('email')}</label>
                    </div>
                    <div className="col-sm-9">
                      <input
                        type="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  
                  {!editingUser && (
                    <div className="row mb-3">
                      <div className="col-sm-3">
                        <label className="form-label">{t('password')}</label>
                      </div>
                      <div className="col-sm-9">
                        <input
                          type="password"
                          className="form-control"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">{t('first.name')}</label>
                    </div>
                    <div className="col-sm-9">
                      <input
                        type="text"
                        className="form-control"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">{t('last.name')}</label>
                    </div>
                    <div className="col-sm-9">
                      <input
                        type="text"
                        className="form-control"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="row mb-3">
                    <div className="col-sm-3">
                      <label className="form-label">{t('role')}</label>
                    </div>
                    <div className="col-sm-9">
                      {availableRoles.map(role => (
                        <div key={role} className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={role}
                            checked={formData.roles.includes(role)}
                            onChange={(e) => handleRoleChange(role, e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor={role}>
                            {role}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    {t('cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingUser ? t('update') : t('create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showModal && <div className="modal-backdrop show"></div>}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reset User Password</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setNewPassword('');
                    setError('');
                  }}
                ></button>
              </div>
              <form onSubmit={handleResetPasswordSubmit}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">{error}</div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">New Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                    <div className="form-text">Password must be at least 6 characters long.</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setNewPassword('');
                      setError('');
                    }}
                  >
                    {t('cancel')}
                  </button>
                  <button type="submit" className="btn btn-warning">
                    Reset Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showResetPasswordModal && <div className="modal-backdrop show"></div>}
    </div>
  );
};

export default UsersPage;